import React, { useRef, useState } from 'react'

interface DocumentViewerProps {
  htmlContent: string
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ htmlContent }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const currentContentRef = useRef<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // Function to check if content has actually changed
  const hasContentChanged = React.useCallback((newContent: string): boolean => {
    return newContent !== currentContentRef.current
  }, [])

  // Update function that minimizes flickering
  const updateContent = React.useCallback(
    (newContent: string): void => {
      if (!iframeRef.current || !hasContentChanged(newContent)) {
        return
      }

      setIsLoading(true)
      const iframe = iframeRef.current
      const doc = iframe.contentDocument || iframe.contentWindow?.document

      if (!doc) {
        setIsLoading(false)
        return
      }

      // Preserve scroll position
      const scrollTop = doc.documentElement?.scrollTop || doc.body?.scrollTop || 0
      const scrollLeft = doc.documentElement?.scrollLeft || doc.body?.scrollLeft || 0

      // Update the document
      doc.open()
      doc.write(newContent)
      doc.close()

      // Restore scroll position after update
      requestAnimationFrame(() => {
        const newDoc = iframe.contentDocument || iframe.contentWindow?.document

        if (newDoc?.documentElement) {
          newDoc.documentElement.scrollTop = scrollTop
          newDoc.documentElement.scrollLeft = scrollLeft
        } else if (newDoc?.body) {
          newDoc.body.scrollTop = scrollTop
          newDoc.body.scrollLeft = scrollLeft
        }

        setTimeout(() => {
          setIsLoading(false)
        }, 50)
      })

      currentContentRef.current = newContent
    },
    [hasContentChanged]
  )

  // Handle updates when content changes
  React.useEffect(() => {
    if (htmlContent) {
      updateContent(htmlContent)
    }
  }, [htmlContent, updateContent])

  // Handle iframe load event
  const handleIframeLoad = React.useCallback((): void => {
    if (htmlContent && hasContentChanged(htmlContent)) {
      updateContent(htmlContent)
    }
  }, [htmlContent, updateContent, hasContentChanged])

  const defaultStyle = {
    width: '100%',
    height: '100%',
    transition: 'opacity 0.2s ease-in-out',
    opacity: isLoading ? 0.7 : 1,
  }

  return (
    <>
      {/* eslint-disable-next-line factorial/no-raw-html */}
      <iframe
        ref={iframeRef}
        title="Document Viewer"
        style={defaultStyle}
        onLoad={handleIframeLoad}
        sandbox="allow-same-origin"
      />
    </>
  )
}

export default DocumentViewer
